import { SAVED_SEARCH_URL_PARAM } from '@aibees/elephant/context/search-params-url-sync';
import { useSearchTableScope } from '@aibees/elephant/context/search-table-scope';
import { useSearchHistoryStore } from '@aibees/elephant/store/search-history-store';
import {
  useSearchParamsStore,
  type SearchTableScope,
} from '@aibees/elephant/store/search-params-store';
import { SearchType } from '@aibees/elephant/types';
import type { SearchBrowseParams } from '@aibees/elephant/types';
import { useReadPresetApi } from '@aibees/shared/api';
import { useNotify } from '@aibees/shared/hooks';
import { objSanitizeNilValues } from '@aibees/shared/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Legacy saved-search presets merged the scope `SearchType` ('Company' | 'People')
 * into their `value` object. That key was never part of the applied filter params
 * (the old `metadata.url` encoded the plain `params`), so strip it before
 * hydrating the store. New saves store clean params and hit neither branch.
 */
function sanitizeSavedSearchValue(
  value?: Partial<SearchBrowseParams>
): Partial<SearchBrowseParams> {
  if (!value) return {};
  const next = { ...value } as Record<string, unknown>;
  if (next.type === SearchType.Company || next.type === SearchType.People) {
    delete next.type;
  }
  return next as Partial<SearchBrowseParams>;
}

/**
 * Reads the one-shot `?saved-search=<presetId>` link param, fetches that saved
 * search preset, replaces the current scope's params with its value, then strips
 * the param from the URL.
 *
 * This is the saved-search counterpart of `useApplySharedSearch`: "View Search"
 * links carry only the short preset id (never the full filter set), so a new tab
 * can restore the saved filters without ever putting a long filter query string
 * in the URL (which is what could hit the web server's URL length limit / 414).
 *
 * The id is LATCHED from the initial URL because `saved-search` is excluded from
 * the filter sync (`omitNonFilterParams`); the sync would otherwise strip it
 * before the async fetch resolves.
 */
export function useApplySavedSearch() {
  const scope = useSearchTableScope();
  const [, setSearchParams] = useSearchParams();
  const replaceParamsForScope = useSearchParamsStore.useReplaceParamsForScope();
  const resetHistoryTo = useSearchHistoryStore.useResetTo();
  const notify = useNotify();

  const [savedSearchId] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get(SAVED_SEARCH_URL_PARAM)
  );
  const appliedRef = useRef(false);

  const hasScope =
    scope === 'company' || scope === 'people' || scope === 'employees';
  const shouldFetch = !!savedSearchId && hasScope && !appliedRef.current;

  const { data, isError, isSuccess } = useReadPresetApi<
    Partial<SearchBrowseParams>
  >(savedSearchId ?? undefined, { enabled: shouldFetch });

  const stripSavedSearchId = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(SAVED_SEARCH_URL_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  useEffect(() => {
    if (!savedSearchId || !hasScope || appliedRef.current) return;

    if (isError) {
      appliedRef.current = true;
      notify.error('This saved search could not be loaded.');
      stripSavedSearchId();
      return;
    }

    if (isSuccess) {
      appliedRef.current = true;
      const applied = sanitizeSavedSearchValue(data?.value);
      replaceParamsForScope(scope as SearchTableScope, applied);
      // Seed the applied state as the undo/redo baseline so the header "undo"
      // returns to the saved search (and can't drop below it into empty filters).
      resetHistoryTo(
        scope as SearchTableScope,
        objSanitizeNilValues(applied) as Record<string, string>
      );
      stripSavedSearchId();
    }
  }, [
    savedSearchId,
    hasScope,
    scope,
    data,
    isError,
    isSuccess,
    replaceParamsForScope,
    resetHistoryTo,
    notify,
    stripSavedSearchId,
  ]);
}
